<template>
  <component
    v-bind="$attrs"
    v-on="$listeners"
    ref="button"
    :is="component"
    :to="to || null"
    :type="type"
    :href="isExternal && to"
    :class="classes"
    :disabled="isLoading || disabled"
    @mouseenter="showPopover = true"
    @mouseleave="showPopover = false"
  >
    <c-icon
      v-if="icon && !isLoading"
      class="icon"
      width="15"
      height="15"
      :icon="icon"
    />

    <c-spinner
      v-if="isLoading"
      size="sm"
      class="spinner"
    />

    <span
      v-if="!isEmpty"
      class="text"
    >
      <slot />
    </span>

    <c-popover
      v-if="popoverLabel && showPopover"
      class="popover"
      :position="['top', 'center']"
      :target="$refs.button"
    >
      <span v-html="popoverLabel" />
    </c-popover>
  </component>
</template>

<script>
import isExternal from '@/helpers/isExternal'

export default {
  props: {
    to: [String, Object],
    icon: String,
    leftIcon: String,
    rightIcon: String,
    flat: Boolean,
    alternative: Boolean,
    disabled: Boolean,
    isNuxt: Boolean,
    isLoading: Boolean,
    popoverLabel: String,
    type: {
      type: String,
      default: 'button',
      validator: (type) => ['submit', 'reset', 'button'].includes(type)
    },
    size: {
      type: String,
      default: 'md',
      validator: (size) => ['sm', 'md', 'lg', 'xl'].includes(size)
    }
  },
  data () {
    return {
      showPopover: false
    }
  },
  computed: {
    isExternal () {
      const result = isExternal(this.to)
      return result
    },
    isEmpty () {
      const isEmpty = !this.$slots.default
      return isEmpty
    },
    component () {
      const component = !this.to ? 'button'
        : this.isExternal ? 'a'
          : this.isNuxt ? 'nuxt-link'
            : 'router-link'
      return component
    },
    classes () {
      const classes = [
        'c-button',
        '-' + this.size,
        {
          '-flat': this.flat,
          '-only-icon': this.isEmpty && this.icon,
          '-disabled': this.isLoading || this.disabled,
          '-alternative': this.alternative
        }
      ]
      return classes
    }
  }
}
</script>

<style lang="scss">
@import "~@styles/convenia-components/reference.scss";

// c-button
$c-sm-button-size: 25px !default;
$c-md-button-size: 30px !default;
$c-lg-button-size: 35px !default;
$c-xl-button-size: 40px !default;
$c-sm-button-padding: $c-sm-button-size !default;
$c-md-button-padding: $c-md-button-size !default;
$c-lg-button-padding: $c-lg-button-size !default;
$c-xl-button-padding: $c-xl-button-size !default;
$c-button-font-family: $c-font-family-base !default;
$c-button-font-weight: 300 !default;
$c-button-background-color: $c-color-primary !default;
$c-button-hover-background-color: color(primary, dark-10) !default;

$c-button-border-color: color(primary, dark-10) !default;

$c-button-active-background-color: color(primary, dark-5) !default;
// c-button > text
$c-button-text-color: #fff !default;
$c-button-text-font-size: 10px !default;

// c-button > icon
$c-button-icon-color: rgba(#fff, 0.5) !default;

// c-button -alternative
$c-button-alt-border: 1px solid #e9eaee !default;
$c-button-alt-text-color: $c-color-link !default;
$c-button-alt-icon-color: darken(#e9eaee, 25) !default;

.c-button {
  & {
    -webkit-font-smoothing: subpixel-antialiased;
    box-sizing: border-box;
    overflow: hidden;
    vertical-align: middle;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: $c-md-button-size;
    min-width: $c-md-button-size;
    border: none;
    border-radius: $c-md-button-size;
    text-decoration: none;
    background-color: $c-button-background-color;
    padding-right: 18px;
    padding-left: 18px;
    cursor: pointer;
    transition: box-shadow 0.2s ease, background-color 0.2s ease;
  }

  &:focus {
    box-shadow: none;
    outline: none;
  }

  &:hover {
    border-color: transparent;
    background-color: $c-button-hover-background-color;
  }

  &:hover > .icon {
    fill: lighten($c-button-icon-color, 15%);
  }

  &:hover > .text {
    color: lighten($c-button-text-color, 15%);
  }

  &:active {
    background: $c-button-active-background-color;
    box-shadow: none;
  }

  &:active > .text {
    color: darken($c-button-text-color, 15%);
  }

  &:active > .icon {
    fill: darken($c-button-icon-color, 15%);
  }

  & > .icon {
    fill: $c-button-icon-color;
    transition: fill 300ms ease;
    position: relative;
    top: -1px;
  }

  & > .spinner {
    position: relative;
    left: -4px;
    top: 1px;
  }

  & > .text {
    text-transform: uppercase;
    font-size: $c-button-text-font-size;
    font-weight: $c-button-font-weight;
    font-family: $c-button-font-family;
    color: $c-button-text-color;
    transition: color 300ms ease;
  }

  &.-only-icon {
    padding: 0 !important;

    & > .icon {
      margin: auto;
    }

    & > .spinner {
      left: 0;
    }
  }

  &:not(.-only-icon) > .icon {
    margin-top: auto;
    margin-bottom: auto;
    margin-right: 6px;
  }

  &.-alternative {
    -webkit-font-smoothing: antialiased;
    background-image: none;
    background-color: transparent;
    border: $c-button-alt-border;
    transition: color 0.5s ease;
    will-change: box-shadow;

    &:active {
      box-shadow: 0 0 0 3px $c-button-alt-text-color;
    }

    @include medium-up {
      &:hover {
        border-color: transparent;
        box-shadow: 0 0 0 3px $c-button-alt-text-color;
      }

      &:active {
        box-shadow: 0 0 0 1px $c-button-alt-text-color;
      }
    }

    &:hover > .text {
      color: color(link);
    }

    &:hover > .icon {
      fill: $c-button-alt-icon-color;
    }
  }

  &.-alternative > .icon {
    fill: $c-button-alt-icon-color;
  }

  &.-alternative > .text {
    font-weight: 700;
    color: $c-button-alt-text-color;
  }

  &.-md > .text {
    line-height: 26px;
  }

  &.-sm > .text {
    line-height: 22px;
  }

  &.-sm {
    height: $c-sm-button-size;
    min-width: $c-sm-button-size;
    border-radius: $c-sm-button-size;
  }

  &.-lg > .text {
    line-height: 32px;
  }

  &.-lg {
    height: $c-lg-button-size;
    min-width: $c-lg-button-size;
    border-radius: $c-lg-button-size;
  }

  &.-xl > .text {
    line-height: 37px;
  }

  &.-xl {
    height: $c-xl-button-size;
    min-width: $c-xl-button-size;
    border-radius: $c-xl-button-size;
  }

  &.-flat {
    -webkit-font-smoothing: antialiased;
    background-color: transparent;
    border: 0;
    border-radius: 0;
    padding: 0;
    height: auto;
    overflow: visible;

    &:hover {
      box-shadow: none;
    }

    &:hover > .text,
    &:hover > .icon {
      color: color(link);
      fill: color(link);
    }

    &:active > .text {
      text-decoration: none;
    }
  }

  &.-flat > .icon {
    fill: #cfd2e0;
  }

  &.-flat > .text {
    line-height: 1;
    color: color(link);
    font-weight: 700;
  }

  &:not(.-flat) > .spinner .c-spinner-circle {
    stroke: #fff;
  }

  &.-disabled {
    cursor: not-allowed;
    background-color: #a0a0a0;
    color: white;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/convenia-components/CButton.vue